import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import ErrorPage from '~/shared/components/ErrorPage';
import LoginPage from '~/modules/auth/LoginPage';
import { AuthCallback } from '~/modules/auth/AuthCallback';
import SponsorLoginPage from '~/modules/sponsor/SponsorLoginPage';
import { SponsorAuthCallback } from '~/modules/sponsor/SponsorAuthCallback';
import SponsorDashboard from '~/modules/sponsor/SponsorDashboard';
import NotFound from '~/shared/components/NotFound';
import Layout from '~/shared/components/Layout';
import { ProtectedRoute } from '~/shared/components/ProtectedRoute';
import { TreeRouteGuard } from '~/shared/components/TreeRouteGuard';
import { ConsentRouteGuard } from '~/shared/components/ConsentRouteGuard';
import { SponsorRouteGuard } from '~/shared/components/SponsorRouteGuard';
import { ROUTES } from '~/shared/constants';
import { isSponsorExperience } from '~/shared/utils/sponsorDomain';
import {
  ConsentScreen,
  EducationalAcknowledgmentScreen,
  QuestionScreen,
  RiskTierResultsScreen,
  MdiExpectationsScreen,
  FamilyTreeConsentScreen,
} from '~/modules/onboarding/pages';
import { OnboardingForm } from '~/modules/onboarding/pages/OnboardingForm';
import InvitationAcceptance from '~/modules/invitation/InvitationAcceptance';

const FamilyTreeViewer = lazy(
  () => import('~/modules/familyTree/FamilyTreeViewer'),
);

const VaultPage = lazy(() => import('~/modules/vault/VaultPage'));
const VaultEntryPage = lazy(
  () => import('~/modules/vault/pages/VaultEntryPage'),
);

// Consumer routes (default experience)
const consumerRoutes: RouteObject[] = [
  {
    path: ROUTES.LOGIN,
    element: <LoginPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.AUTH_CALLBACK,
    element: <AuthCallback />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.INVITATION,
    element: <InvitationAcceptance />,
    errorElement: <ErrorPage />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: ROUTES.CONSENT,
        element: <ConsentScreen />,
        errorElement: <ErrorPage />,
      },
      {
        path: ROUTES.ONBOARDING.CONSENT,
        element: <EducationalAcknowledgmentScreen />,
        errorElement: <ErrorPage />,
      },
      {
        path: ROUTES.ONBOARDING.QUESTIONS,
        element: <QuestionScreen />,
        errorElement: <ErrorPage />,
      },
      {
        path: ROUTES.ONBOARDING.RESULTS,
        element: <RiskTierResultsScreen />,
        errorElement: <ErrorPage />,
      },
      {
        path: ROUTES.ONBOARDING.TESTING_EXPECTATIONS,
        element: <MdiExpectationsScreen />,
        errorElement: <ErrorPage />,
      },
      {
        path: ROUTES.ONBOARDING.TREE_PROFILE_CONSENT,
        element: <FamilyTreeConsentScreen />,
        errorElement: <ErrorPage />,
      },
      {
        element: <Layout />,
        children: [
          {
            element: <ConsentRouteGuard />,
            children: [
              {
                path: ROUTES.ONBOARDING.ROOT,
                element: <OnboardingForm />,
                errorElement: <ErrorPage />,
              },
            ],
          },
          {
            path: ROUTES.VAULT,
            element: (
              <Suspense fallback={null}>
                <VaultPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: ROUTES.VAULT_NEW,
            element: (
              <Suspense fallback={null}>
                <VaultEntryPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: ROUTES.VAULT_EDIT,
            element: (
              <Suspense fallback={null}>
                <VaultEntryPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            element: <TreeRouteGuard />,
            children: [
              {
                path: ROUTES.TREE,
                element: (
                  <Suspense fallback={null}>
                    <FamilyTreeViewer />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.NOT_FOUND,
    element: <NotFound />,
  },
];

// Sponsor routes (sponsor subdomain or VITE_SPONSOR_MODE=true)
const sponsorRoutes: RouteObject[] = [
  {
    path: ROUTES.LOGIN,
    element: <SponsorLoginPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.AUTH_CALLBACK,
    element: <SponsorAuthCallback />,
    errorElement: <ErrorPage />,
  },
  {
    element: <SponsorRouteGuard />,
    children: [
      {
        path: ROUTES.SPONSOR_DASHBOARD,
        element: <SponsorDashboard />,
        errorElement: <ErrorPage />,
      },
    ],
  },
  {
    path: ROUTES.NOT_FOUND,
    element: <NotFound />,
  },
];

// Conditionally export routes based on sponsor experience
const routes: RouteObject[] = isSponsorExperience()
  ? sponsorRoutes
  : consumerRoutes;

export { routes };
